import { create } from 'zustand';
import type { ConversationContext } from '@/services/conversation';

export interface ConversationMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

export interface Citation {
  title: string;
  slug: string;
  entityType: string;
}

interface ConversationState {
  conversationId: string | null;
  messages: ConversationMessage[];
  isStreaming: boolean;
  streamingContent: string;
  context: ConversationContext;
  suggestedQuestions: string[];
  citations: Citation[];
  error: string | null;
  drawerOpen: boolean;

  setConversationId: (id: string | null) => void;
  setContext: (context: ConversationContext) => void;
  addMessage: (message: ConversationMessage) => void;
  startStreaming: () => void;
  appendChunk: (token: string) => void;
  endStreaming: (fullContent: string) => void;
  setSuggestedQuestions: (questions: string[]) => void;
  setCitations: (citations: Citation[]) => void;
  setError: (error: string | null) => void;
  openDrawer: () => void;
  closeDrawer: () => void;
  reset: () => void;
}

const initialState = {
  conversationId: null,
  messages: [],
  isStreaming: false,
  streamingContent: '',
  context: {},
  suggestedQuestions: [],
  citations: [],
  error: null,
  drawerOpen: false,
};

export const useConversationStore = create<ConversationState>()((set) => ({
  ...initialState,

  setConversationId: (id) => set({ conversationId: id }),

  setContext: (context) => set({ context }),

  addMessage: (message) =>
    set((state) => ({ messages: [...state.messages, message] })),

  startStreaming: () =>
    set({ isStreaming: true, streamingContent: '', error: null }),

  appendChunk: (token) =>
    set((state) => ({ streamingContent: state.streamingContent + token })),

  endStreaming: (fullContent) =>
    set((state) => ({
      isStreaming: false,
      streamingContent: '',
      messages: [
        ...state.messages,
        {
          id: crypto.randomUUID(),
          role: 'assistant' as const,
          content: fullContent,
          timestamp: new Date().toISOString(),
        },
      ],
    })),

  setSuggestedQuestions: (questions) => set({ suggestedQuestions: questions }),

  setCitations: (citations) => set({ citations }),

  setError: (error) => set({ error, isStreaming: false }),

  openDrawer: () => set({ drawerOpen: true }),

  closeDrawer: () => set({ drawerOpen: false }),

  reset: () => set({ ...initialState, drawerOpen: false }),
}));
